/**
 * ChatTextRow — Textarea with character counter.
 *
 * Clean design: borderless textarea with auto-resize and char counter.
 * Send button lives in the parent toolbar for DOM ordering.
 */

import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { WavRenderer } from "../utils/wavRenderer";

const ChatTextRow = ({
  inputValue,
  onInputChange,
  onKeyDown,
  isOverLimit,
  maxMsgLength,
  primaryColor,
  audioRecorder,
  isConnected,
  isTranscribingAudio,
  transcribingDots,
  isLoading,
  activatingMatrix,
  allowAsk,
  textareaRef: externalTextareaRef,
}) => {
  const { t } = useTranslation();
  const localTextareaRef = useRef(null);
  // Optional external ref so the parent can focus the textarea (e.g. after a conversation starter
  // fills it). Falls back to the local one when no ref is passed down.
  const textareaRef = externalTextareaRef || localTextareaRef;
  const containerRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(300);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 150) + "px";
    }
  }, [inputValue, textareaRef]);

  // Auto-focus textarea when input becomes enabled (agent finished responding)
  useEffect(() => {
    const disabled = isLoading || activatingMatrix || allowAsk === false || isConnected || isTranscribingAudio;
    if (!disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isLoading, activatingMatrix, allowAsk, isConnected, isTranscribingAudio, textareaRef]);

  // Measure container width for audio waveform rendering
  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCanvasWidth(entry.contentRect.width - 40);
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="flex items-end gap-2 px-3.5 pt-3.5 pb-2">
      {audioRecorder?.isRecording ? (
        <div className="flex-1 flex items-center min-h-[24px]">
          <WavRenderer
            audioData={audioRecorder.audioData}
            color={primaryColor}
            width={canvasWidth}
            height={22}
            barWidth={4}
            barSpacing={2}
            center
          />
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          className="flex-1 resize-none outline-none border-none bg-transparent min-h-[24px] max-h-[150px] font-sans text-[15px] leading-[1.5] text-gray-900 placeholder:text-gray-400 disabled:opacity-50"
          placeholder={
            isTranscribingAudio
              ? `${t("transcribing")}${transcribingDots}`
              : isConnected
                ? t("pressAndHoldToRecord")
                : t("replyPlaceholder")
          }
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={isLoading || activatingMatrix || allowAsk === false || isConnected || isTranscribingAudio}
          rows={1}
          style={{
            fontStyle: isTranscribingAudio ? "italic" : "normal",
            color: isTranscribingAudio ? "#999" : undefined,
          }}
        />
      )}

      {/* Char counter */}
      {!isConnected && !audioRecorder?.isRecording && inputValue.length > 0 && (
        <span className={`text-xs shrink-0 ${isOverLimit ? "text-red-500" : "text-gray-400"}`}>
          {inputValue.length}/{maxMsgLength}
        </span>
      )}
    </div>
  );
};

export { ChatTextRow };
export default ChatTextRow;
